import { getLoopWindTransaction, IncreaseLeverageTransactionInput } from "@bridgesplit/abf-sdk";
import { combineTransactionPromises, TransactionActionType, useUserTokens } from "@bridgesplit/react";
import { Result, TransactionStatus, uiAmountToLamports } from "@bridgesplit/utils";

import { useAbfFetches } from "../reducers";
import { AbfTransactionDetails, AbfGeneratorResult, LoopExpanded, TransactionSenderOptions } from "../types";
import { useAbfGenerateTransactionWithData } from "./common";
import { getMintFromAssetData } from "../utils";
import { isPrincipalTokenWind, isWhirlpoolLoop } from "../api/loop";

export type LoopWindParams = IncreaseLeverageTransactionInput & {
    loopExpanded: LoopExpanded;
    userPublicKey: string;
    leverageMultiplier: number;
    depositMint: string;
    isInitialWind: boolean;
};

export function useLoopWindTransaction(): AbfTransactionDetails<LoopWindParams> {
    const generate = useAbfGenerateTransactionWithData();
    const { resetNapoleonApi, resetMarketsPublicApi, resetLoopApi, resetLoanApi } = useAbfFetches();
    const { refresh } = useUserTokens();

    async function getTransactionsWithParams({ ...params }: LoopWindParams): AbfGeneratorResult {
        const { loopExpanded, userPublicKey, leverageMultiplier } = params;

        try {
            const principalDecimals = loopExpanded.principalToken.decimals;
            const create = generate({
                generateFunction: getLoopWindTransaction,
                identifier: "Create position",
                getActions: (response) => {
                    if (params.isInitialWind) {
                        return [
                            {
                                action: {
                                    [TransactionActionType.WriteLoopWind]: {
                                        mintsAndOwners: getMintsAndOwners(params, userPublicKey),
                                        loanAddress: response.loanAddress,
                                        newLeverageMultiplier: leverageMultiplier
                                    }
                                },
                                transactionStatus: TransactionStatus.Confirmed,
                                transactionIndexes: [0]
                            }
                        ];
                    } else {
                        return [];
                    }
                },
                params: {
                    ...params,
                    principalRequested: params.principalRequested.map((param) => ({
                        ledgerIndex: param.ledgerIndex,
                        principalAmount: uiAmountToLamports(param.principalAmount, principalDecimals),
                        principalMint: param.principalMint,
                        strategy: param.strategy,
                        durationIndex: param.durationIndex,
                        expectedLoanValues: param.expectedLoanValues
                    })),
                    depositCollateral: params.depositCollateral.map((collateral) => {
                        return {
                            collateralAmount: uiAmountToLamports(
                                collateral.collateralAmount,
                                loopExpanded.collateralToken.decimals
                            ),
                            collateralAssetData: collateral.collateralAssetData,
                            loanCreationParams: collateral.loanCreationParams,
                            weightMatrixUpdate: collateral.weightMatrixUpdate ?? [1_000_000, 0, 0, 0, 0] // Ledger 0: 100% of collateral 0, 0% of others
                        };
                    })
                }
            });

            const transactions = await combineTransactionPromises([create], {
                order: "multiStep",
                commitmentLevel: "finalized"
            });
            return transactions;
        } catch (error) {
            return Result.err(error);
        }
    }

    const sendOptions: TransactionSenderOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            resetLoopApi();
            void refresh();
            resetLoanApi();
        },
        minGeyserConfirmations: 5
    };

    return { getTransactionsWithParams, sendOptions, description: "Initializing position" };
}

function getMintsAndOwners(params: LoopWindParams, userPublicKey: string): [string, string][] {
    const depositMint = params.depositMint;
    const isPrincipalDeposit =
        isPrincipalTokenWind(params.loopExpanded.principalToken.mint, depositMint) &&
        !isWhirlpoolLoop(params.loopExpanded);

    if (isPrincipalDeposit) {
        return [[depositMint, userPublicKey]];
    }

    return params.depositCollateral.map((collateral) => [
        getMintFromAssetData(collateral.collateralAssetData),
        userPublicKey
    ]);
}
